import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// ─── MOCREO hub admin (owner-only) ──────────────────────────────────────────
// Modal shown from Equipment view → "🛰 Hubs" button. Lists configured
// hubs with online state + remove action, plus a multi-step "Add a hub"
// flow: collect email/password → fetch Asset list → owner picks Asset →
// backend mints API key, encrypts, persists, addAsset()s into running
// client. The password never leaves the form's local React state.
export function HubAdminModal({ currentUser, onClose }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [step, setStep] = React.useState('list'); // 'list' | 'creds' | 'pick' | 'done'
  const [creds, setCreds] = React.useState({ email: '', password: '', location_id: '' });
  const [assets, setAssets] = React.useState([]);
  const [pickAsset, setPickAsset] = React.useState('');
  const [locations, setLocations] = React.useState([]);

  async function refresh() {
    try {
      const r = await api('GET', '/api/admin/sensor-providers', null, headers);
      setData(r);
    } catch (e) {
      setErr(e.message || String(e));
      setData({ providers: [], hubs: [] }); // unblock the "Loading…" path on error
    }
  }
  React.useEffect(() => {
    refresh();
    api('GET', '/api/locations').then(setLocations).catch(() => setLocations([]));
  }, []);

  function clearCreds() {
    setCreds({ email: '', password: '', location_id: '' });
    setAssets([]);
    setPickAsset('');
  }

  async function nextFromCreds(e) {
    e.preventDefault();
    if (!creds.email || !creds.password || !creds.location_id) return;
    setBusy(true); setErr('');
    try {
      const r = await api('POST', '/api/admin/sensor-providers/preview',
        { email: creds.email, password: creds.password }, headers);
      setAssets(r.assets || []);
      setStep('pick');
    } catch (e) { setErr(e.message || 'login failed'); }
    finally { setBusy(false); }
  }

  async function saveHub() {
    if (!pickAsset) return;
    setBusy(true); setErr('');
    try {
      await api('POST', '/api/admin/sensor-providers', {
        email: creds.email,
        password: creds.password,
        location_id: Number(creds.location_id),
        asset_id: pickAsset,
        display_name: `empire-monarch-${creds.location_id}`,
      }, headers);
      clearCreds();
      setStep('list');
      refresh();
    } catch (e) { setErr(e.message || 'save failed'); }
    finally { setBusy(false); }
  }

  async function removeHub(assetId) {
    if (!confirm('Unlink this hub? Sensors at this location will stop polling. The encrypted key is also wiped.')) return;
    setBusy(true); setErr('');
    try {
      await api('DELETE', `/api/admin/sensor-providers/${encodeURIComponent(assetId)}`, null, headers);
      refresh();
    } catch (e) { setErr(e.message || 'remove failed'); }
    finally { setBusy(false); }
  }

  const providers = data?.providers || [];
  const hubsByAssetId = new Map();
  for (const h of (data?.hubs || [])) hubsByAssetId.set(h.assetId || h.asset_id || '', h);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                    zIndex: 60, padding: 24, overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560 }}>
        <div style={{ padding: 16, borderBottom: '1px solid #f0f0f0',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>MOCREO hubs</div>
            <div style={{ fontSize: 11, color: '#9e9e9e' }}>One hub per location. Owner only.</div>
          </div>
          <button onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: 22,
                      color: '#9e9e9e', cursor: 'pointer' }}>×</button>
        </div>

        {err && <div style={{ margin: 16, padding: 10, background: '#fee', color: '#b91c1c', borderRadius: 8, fontSize: 13 }}>{err}</div>}

        {step === 'list' && (
          <div style={{ padding: 16 }}>
            {!data ? (
              <div style={{ fontSize: 13, color: '#9e9e9e' }}>Loading…</div>
            ) : providers.length === 0 ? (
              <div style={{ fontSize: 13, color: '#9e9e9e', fontStyle: 'italic' }}>
                No hubs configured yet. Add one to start polling sensors at a location.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {providers.map(p => (
                  <div key={p.id} style={{ padding: 12, border: '1px solid #e0e0e0', borderRadius: 8,
                                              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{p.location_name || `Location ${p.location_id}`}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>
                        Asset {p.asset_id}
                        {p.display_name ? ` · ${p.display_name}` : ''}
                      </div>
                      <div style={{ fontSize: 11, color: '#9e9e9e' }}>
                        Added {new Date(p.created_at).toLocaleDateString()} by {p.created_by_name || 'unknown'}
                      </div>
                    </div>
                    <button onClick={() => removeHub(p.asset_id)} disabled={busy}
                      style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600,
                                background: '#fff', color: '#dc2626',
                                border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}>
                      Unlink
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setStep('creds')}
                style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600,
                          background: '#1d4ed8', color: '#fff',
                          border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                + Add a hub
              </button>
            </div>
          </div>
        )}

        {step === 'creds' && (
          <form onSubmit={nextFromCreds} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Enter your MOCREO Smart account credentials. We'll list your Assets so you can pick which one this café is.
              The password is used once to mint a per-Asset API key and is then dropped from memory.
            </div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Location
              <select value={creds.location_id} onChange={e => setCreds(c => ({ ...c, location_id: e.target.value }))} required
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }}>
                <option value="">— pick a café —</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              MOCREO email
              <input type="email" value={creds.email} onChange={e => setCreds(c => ({ ...c, email: e.target.value }))} required autoComplete="off"
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              MOCREO password
              <input type="password" value={creds.password} onChange={e => setCreds(c => ({ ...c, password: e.target.value }))} required autoComplete="new-password"
                style={{ display: 'block', width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, marginTop: 4 }} />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button type="button" onClick={() => { clearCreds(); setStep('list'); }}
                style={{ padding: '8px 14px', fontSize: 13, color: '#6b7280', background: '#fff', border: '1px solid #d0d4d9', borderRadius: 8, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="submit" disabled={busy}
                style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#fff', background: '#1d4ed8', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                {busy ? 'Logging in…' : 'Next'}
              </button>
            </div>
          </form>
        )}

        {step === 'pick' && (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Pick the MOCREO Asset that represents this café.
            </div>
            <select value={pickAsset} onChange={e => setPickAsset(e.target.value)}
              style={{ width: '100%', padding: 8, border: '1px solid #d0d4d9', borderRadius: 6 }}>
              <option value="">— pick an asset —</option>
              {assets.map(a => <option key={a.assetId} value={a.assetId}>{a.name} · {a.assetId}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={() => setStep('creds')}
                style={{ padding: '8px 14px', fontSize: 13, color: '#6b7280', background: '#fff', border: '1px solid #d0d4d9', borderRadius: 8, cursor: 'pointer' }}>
                Back
              </button>
              <button onClick={saveHub} disabled={busy || !pickAsset}
                style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#fff', background: '#16a34a', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                {busy ? 'Linking…' : 'Link this hub'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
